/**
 * useCustomerServices Hook
 *
 * Real-time subscription to customer services data from Firestore
 * Provides filtering by customer, status, and service type
 * Calculates MRR (Monthly Recurring Revenue) for customers
 */

import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, QueryConstraint } from 'firebase/firestore';
import { db } from '@/firebase';
import { CustomerService } from '@/types';
import useTenantId, { tenantIdReadUnion } from '@/hooks/useTenantId';
import { createdAtMs } from '@/utils/sortHelpers';

interface UseCustomerServicesOptions {
  customerId?: string;
  status?: CustomerService['status'] | CustomerService['status'][];
  serviceType?: CustomerService['serviceType'];
  includeInactive?: boolean;
  /**
   * Set false to skip the subscription entirely and report an empty list.
   * Used by feature-pack gating: `customer_services` is an MSP-pack surface,
   * and callers mounted for every tenant (global search, the directors
   * cashflow panel) must not open the listener for a tenant that has no
   * module to view the results in. Defaults to true — existing callers are
   * unaffected. docs/FEATURE_PACKS_PLAN.md.
   */
  enabled?: boolean;
}

interface UseCustomerServicesReturn {
  services: CustomerService[];
  loading: boolean;
  error: Error | null;
  activeCount: number;
  inactiveCount: number;
  totalMRR: number;
}

/**
 * Main hook - Real-time subscription to customer services
 */
export function useCustomerServices(options: UseCustomerServicesOptions = {}): UseCustomerServicesReturn {
  const tenantId = useTenantId();
  const [services, setServices] = useState<CustomerService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const enabled = options.enabled !== false;

  useEffect(() => {
    if (!enabled) {
      setServices([]);
      setLoading(false);
      setError(null);
      return;
    }
    const constraints: QueryConstraint[] = [
      // Required for strict tenant-isolation rules
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    ];

    // Filter by customer
    if (options.customerId) {
      constraints.push(where('customerId', '==', options.customerId));
    }

    // Filter by status
    if (options.status) {
      if (Array.isArray(options.status)) {
        constraints.push(where('status', 'in', options.status));
      } else {
        constraints.push(where('status', '==', options.status));
      }
    } else if (!options.includeInactive) {
      // Default: exclude Cancelled and Inactive services
      constraints.push(where('status', 'in', ['Active', 'Pending Install', 'Suspended']));
    }

    // Filter by service type
    if (options.serviceType) {
      constraints.push(where('serviceType', '==', options.serviceType));
    }

    // NOTE: no `orderBy('createdAt')` in the query — it silently drops services
    // missing that field. Sort client-side (newest first) so field-absent docs
    // still surface in global search.
    const q = query(collection(db, 'customer_services'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const servicesData = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }) as CustomerService)
          .filter((s) => !s.deleted)
          .sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt));

        setServices(servicesData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching customer services:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [enabled, tenantId, options.customerId, options.status, options.serviceType, options.includeInactive]);

  // Calculate metrics
  const activeCount = useMemo(
    () => services.filter(s => s.status === 'Active').length,
    [services]
  );

  const inactiveCount = useMemo(
    () => services.filter(s => s.status === 'Cancelled' || s.status === 'Inactive').length,
    [services]
  );

  const totalMRR = useMemo(
    () => {
      return services
        .filter(s => s.status === 'Active' && s.monthlyPrice)
        .reduce((sum, service) => sum + (service.monthlyPrice || 0), 0);
    },
    [services]
  );

  return {
    services,
    loading,
    error,
    activeCount,
    inactiveCount,
    totalMRR
  };
}

/**
 * Hook for a single customer's services
 */
export function useCustomerServicesByCustomer(customerId: string | null): UseCustomerServicesReturn {
  return useCustomerServices({
    customerId: customerId || undefined,
    includeInactive: true
  });
}

/**
 * Hook for active services only
 */
export function useActiveCustomerServices(customerId?: string): UseCustomerServicesReturn {
  return useCustomerServices({
    customerId,
    status: 'Active'
  });
}

/**
 * Hook for pending installations
 */
export function usePendingInstallations(): UseCustomerServicesReturn {
  return useCustomerServices({
    status: 'Pending Install'
  });
}

/**
 * Hook for suspended services (requiring attention)
 */
export function useSuspendedServices(): UseCustomerServicesReturn {
  return useCustomerServices({
    status: 'Suspended'
  });
}

/**
 * Calculate total MRR for all active services
 */
export function useGlobalMRR(): { totalMRR: number; loading: boolean } {
  const { totalMRR, loading } = useCustomerServices({
    status: 'Active',
    includeInactive: false
  });

  return { totalMRR, loading };
}

/**
 * Get service count by type for analytics
 */
export function useServiceCountByType(): {
  counts: Record<string, number>;
  loading: boolean;
} {
  const { services, loading } = useCustomerServices({
    status: 'Active'
  });

  const counts = useMemo(() => {
    return services.reduce((acc, service) => {
      const type = service.serviceType;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [services]);

  return { counts, loading };
}
